import { supabase } from './supabase';

export async function sendPushNotification(
  userId: string,
  title: string,
  body: string,
  data?: Record<string, string>
) {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-push-notification`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        title,
        body,
        data
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to send notification: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error sending push notification:', error);
    return null;
  }
}

export async function registerDeviceToken(
  userId: string,
  deviceToken: string,
  deviceType: 'ios' | 'android'
) {
  try {
    const { data: existing } = await supabase
      .from('device_tokens')
      .select('id')
      .eq('user_id', userId)
      .eq('device_token', deviceToken)
      .maybeSingle();

    if (existing) {
      await supabase
        .from('device_tokens')
        .update({
          is_active: true,
          last_used_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', existing.id);
    } else {
      await supabase
        .from('device_tokens')
        .insert({
          user_id: userId,
          device_token: deviceToken,
          device_type: deviceType,
          is_active: true
        });
    }

    return true;
  } catch (error) {
    console.error('Error registering device token:', error);
    return false;
  }
}

export async function getNotificationSettings(userId: string) {
  try {
    let { data, error } = await supabase
      .from('notification_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;

    if (!data) {
      const { data: newSettings } = await supabase
        .from('notification_settings')
        .insert({
          user_id: userId
        })
        .select()
        .single();

      return newSettings;
    }

    return data;
  } catch (error) {
    console.error('Error getting notification settings:', error);
    return null;
  }
}

export async function updateNotificationSettings(
  userId: string,
  settings: {
    enable_lesson_completion?: boolean;
    enable_streak_reminders?: boolean;
    enable_daily_reminders?: boolean;
    daily_reminder_time?: string;
    enable_streak_milestones?: boolean;
  }
) {
  try {
    const updateData = {
      ...settings,
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('notification_settings')
      .update(updateData)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating notification settings:', error);
    return null;
  }
}

export async function checkShouldSendNotification(
  userId: string,
  notificationType: 'lesson_completion' | 'streak_reminder' | 'daily_reminder' | 'streak_milestone'
): Promise<boolean> {
  const settings = await getNotificationSettings(userId);
  if (!settings) return false;

  const settingsMap = {
    'lesson_completion': settings.enable_lesson_completion,
    'streak_reminder': settings.enable_streak_reminders,
    'daily_reminder': settings.enable_daily_reminders,
    'streak_milestone': settings.enable_streak_milestones
  };

  return settingsMap[notificationType] ?? false;
}
